import { Reveal } from "@/components/reveal"

import type { BrutalistHeroContent, LandingHeroAccount } from "@/domains/landing-page/interfaces"

/**
 * Editorial "blue/black brutalist" hero from the scale-3d-head-hero brainstorm.
 * A marble Greek bust (shipped as a transparent cut-out) sits on a stepped field
 * of electric-blue blocks over a near-black blueprint grid, with an oversized
 * condensed headline to the left. The headline renders above the bust so the
 * trailing letters read cleanly where they meet the figure, matching the mock.
 */
export const BrutalistBustHeroView = ({
  content,
  account,
}: {
  readonly content: BrutalistHeroContent
  readonly account?: LandingHeroAccount
}) => {
  const ctaLabel = account ? account.ctaLabel : content.ctaLabel
  const ctaHref = account ? account.dashboardHref : content.ctaHref
  return (
    <section className="relative isolate overflow-hidden bg-[#06070d]">
      <BlueprintGridLayer />

      {/* All composition pieces (blue blocks, bust, headline) share one centered
          container so their proportions stay locked together across screen widths.
          Below the container width the whole hero scales with the viewport; above
          it the composition centers with the grid bleeding into the side gutters. */}
      <div className="relative mx-auto w-full max-w-[1440px]">
        <BluePanelStackLayer />

        {/* Bust cut-out: bottom-anchored at the container's right edge, bleeding a
            little past it so the right shoulder crops, as in the mock. */}
        <div className="pointer-events-none absolute bottom-0 right-1/2 z-10 h-[44vh] max-h-[860px] w-auto translate-x-1/2 sm:right-[-6%] sm:h-[86%] sm:translate-x-0 lg:right-[-6%] lg:h-[94%]">
          <img
            src={content.bustImageSrc}
            alt="Marble bust of a bearded figure looking upward"
            width={1168}
            height={880}
            fetchPriority="high"
            decoding="async"
            className="h-full w-auto object-contain object-bottom drop-shadow-[0_8px_30px_rgba(0,0,0,0.45)]"
          />
        </div>

        <div className="relative z-20 flex min-h-[100svh] flex-col justify-start px-4 pb-[46vh] pt-28 sm:min-h-[80vh] sm:px-6 sm:pb-20 sm:pt-[16vh] lg:min-h-[88vh] lg:px-8 lg:pt-[20vh]">
          <div className="max-w-[560px] lg:max-w-[52%]">
            <Reveal>
              <h1 className="font-display-condensed text-[clamp(2.5rem,8vw,7rem)] uppercase leading-[0.86] tracking-[0.005em] text-t100 [text-wrap:balance]">
                {content.headlineLines.map((line, index) => (
                  <span key={line} className={index === 0 ? "block" : "mt-[0.05em] block"}>
                    {line}
                  </span>
                ))}
              </h1>
            </Reveal>

            <Reveal delayMs={140} className="mt-8">
            <div className="space-y-1 text-lg font-medium text-t300 md:text-xl">
              {content.subcopyLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </Reveal>

          {account?.hasLocks ? (
            <Reveal delayMs={200} className="mt-8">
              <HeroAccountPanel account={account} />
            </Reveal>
          ) : null}

          <Reveal delayMs={240} className="mt-8">
            <a
              href={ctaHref}
              className="group inline-flex items-center gap-3 border-b-2 border-[hsl(var(--hero-accent)/0.7)] pb-1 text-base font-bold uppercase tracking-[0.06em] text-[hsl(var(--hero-accent))] transition-colors hover:border-[hsl(var(--hero-accent))] hover:text-[hsl(var(--hero-accent-bright))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {ctaLabel}
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
                className="transition-transform duration-200 group-hover:translate-x-1"
              >
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            </a>
          </Reveal>
          </div>
        </div>
      </div>

      {/* Full-bleed fade that eases the black hero into the grey page background
          below. Tall + multi-stop so the transition reads as a gentle gradient
          rather than a hard seam, and spans the gutters on ultrawide screens. */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[15] h-72 bg-gradient-to-b from-transparent via-p500/60 to-p500"
        aria-hidden
      />
    </section>
  )
}

/**
 * Connected-wallet snapshot shown inside the hero: a compact glass panel with
 * the user's veSCALE power, locked value, and claimable rewards. All values are
 * real on-chain data supplied by the composite.
 */
const HeroAccountPanel = ({ account }: { readonly account: LandingHeroAccount }) => {
  return (
    <div className="max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm">
      <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-t300">
        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--hero-accent))]" />
        Connected · <span className="font-mono normal-case tracking-normal text-t200">{account.addressLabel}</span>
      </p>
      <div className="grid grid-cols-3 gap-3">
        <HeroAccountStat label="veSCALE power" value={account.veScalePowerLabel} />
        <HeroAccountStat label="veNFTs" value={account.veNftCountLabel} />
        <HeroAccountStat
          label="Claimable"
          value={account.claimableValueLabel}
          accent={account.hasClaimable}
        />
      </div>
    </div>
  )
}

const HeroAccountStat = ({
  label,
  value,
  accent = false,
}: {
  readonly label: string
  readonly value: string
  readonly accent?: boolean
}) => {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-t300">{label}</p>
      <p
        className={`mt-1 truncate text-base font-bold tabular-nums ${accent ? "text-[hsl(var(--hero-accent))]" : "text-t100"}`}
      >
        {value}
      </p>
    </div>
  )
}

/**
 * Stepped composition of electric-blue blocks behind the bust. Multiple offset
 * rectangles with slightly different blue tones create the layered, seamed look
 * from the reference instead of a single flat square.
 */
const BluePanelStackLayer = () => {
  return (
    <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden>
      {/* Faint blueprint panel behind the headline. */}
      <div className="absolute left-[2%] right-[52%] top-[13%] h-[48%] border border-white/[0.06] bg-white/[0.012]" />
      {/* Darker block sitting behind, peeking top-right. */}
      <div className="absolute right-[4%] top-[14%] bottom-[18%] w-[34%] bg-primary-700" />
      {/* Main bright blue field. */}
      <div className="absolute left-[50%] right-[16%] top-[22%] bottom-0 bg-primary" />
      {/* Lower-left step that juts toward the headline (contiguous with the
          main field at the 50% seam, forming a stepped silhouette). */}
      <div className="absolute left-[38%] right-[50%] top-[52%] bottom-0 bg-primary" />
    </div>
  )
}

const CROSSHAIR_POSITIONS = [
  { top: "16%", left: "5%" },
  { top: "38%", left: "29%" },
  { top: "20%", left: "50%" },
  { top: "70%", left: "86%" },
  { bottom: "10%", left: "5%" },
  { bottom: "8%", left: "30%" },
] as const

const BlueprintGridLayer = () => {
  return (
    <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
      <div
        className="absolute inset-0 opacity-[0.6]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgb(120 140 255 / 0.06) 1px, transparent 1px), linear-gradient(to bottom, rgb(120 140 255 / 0.06) 1px, transparent 1px)",
          backgroundSize: "120px 120px",
          maskImage: "radial-gradient(ellipse 80% 80% at 35% 40%, black 30%, transparent 85%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 80% at 35% 40%, black 30%, transparent 85%)",
        }}
      />
      {CROSSHAIR_POSITIONS.map((position, index) => (
        <span key={index} className="hero-crosshair absolute text-t300/40" style={position}>
          <span className="hero-crosshair-ring" style={{ animationDelay: `${index * 0.9}s` }} aria-hidden />
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 4v16" />
            <path d="M4 12h16" />
          </svg>
        </span>
      ))}
    </div>
  )
}
